import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { ArrowRight, X } from 'lucide-react';
import { useOnboarding } from '../onboarding/OnboardingContext';
import { GUIDE_WALKTHROUGH_SLIDES } from '../../content/featureGuideContent';

const SLIDE_ICONS = ['🌳', '✍️', '📖', '📷', '📕', '🔔', '🕯️', '🔒'];

/**
 * First-time welcome walkthrough — calm, skippable, reopenable.
 */
export function GuideWelcomeModal({ isOpen, onClose, onBeginSetup, onExplore }) {
  const [currentStep, setCurrentStep] = useState(0);
  const { markWelcomeSeen } = useOnboarding();
  const navigate = useNavigate();

  const finish = (after) => {
    markWelcomeSeen();
    onClose?.();
    after?.();
  };

  const handleSkip = () => finish();

  const handleExplore = () => finish(() => {
    onExplore?.();
  });

  const handleViewGuide = () => finish(() => navigate('/guide'));

  const handleBeginSetup = () => finish(() => {
    if (typeof onBeginSetup === 'function') onBeginSetup();
  });

  const totalSteps = GUIDE_WALKTHROUGH_SLIDES.length + 1;
  const isWelcomeStep = currentStep === 0;
  const slide = !isWelcomeStep ? GUIDE_WALKTHROUGH_SLIDES[currentStep - 1] : null;
  const isLast = currentStep === totalSteps - 1;

  useEffect(() => {
    if (isOpen) setCurrentStep(0);
  }, [isOpen]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleSkip();
      }}
    >
      <DialogContent
        className="max-h-[90vh] overflow-hidden border border-[#e8e2d8] bg-[#fffcf7] p-0 sm:max-w-lg"
        data-testid="guide-welcome-modal"
      >
        <button
          type="button"
          onClick={handleSkip}
          className="absolute right-4 top-4 z-10 text-[#8b7355] transition hover:text-[#5c4a32]"
          aria-label="Close"
          data-testid="guide-welcome-skip"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="max-h-[calc(90vh-1rem)] overflow-y-auto px-6 pb-8 pt-11 sm:px-8">
          {isWelcomeStep ? (
            <div className="mb-6 text-center">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8b7355]">
                MyLegacyBridge Guide
              </p>
              <DialogTitle className="mt-3 font-serif text-2xl font-normal text-[#1c1917]">
                Welcome to your family archive
              </DialogTitle>
              <DialogDescription className="mt-3 text-[0.95rem] leading-relaxed text-[#5c534a]">
                What you can preserve, create and share — at your own pace.
              </DialogDescription>
            </div>
          ) : (
            <div className="mb-6 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#c9a961]/12 text-2xl ring-1 ring-[#c9a961]/20">
                {SLIDE_ICONS[currentStep - 1] || '•'}
              </div>
              <DialogTitle className="font-serif text-xl font-normal text-[#1c1917]">{slide.title}</DialogTitle>
              <DialogDescription className="mt-3 text-sm leading-relaxed text-[#5c534a]">{slide.body}</DialogDescription>
            </div>
          )}

          <div className="mb-6 flex justify-center gap-1.5">
            {Array.from({ length: totalSteps }).map((_, index) => (
              <div
                key={index}
                className={`h-1.5 w-1.5 rounded-full transition-colors ${
                  index === currentStep ? 'bg-[#c9a961]' : 'bg-[#e8e2d8]'
                }`}
              />
            ))}
          </div>

          {!isLast ? (
            <div className="flex gap-3">
              {currentStep > 0 ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="flex-1 text-[#5c534a]"
                  onClick={() => setCurrentStep((s) => s - 1)}
                >
                  Back
                </Button>
              ) : null}
              <Button
                type="button"
                className={`${currentStep === 0 ? 'w-full' : 'flex-1'} rounded-full bg-[#1c1917] text-[#faf7f2] hover:bg-[#2c2824]`}
                onClick={() => setCurrentStep((s) => s + 1)}
                data-testid="guide-welcome-next"
              >
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <Button
                type="button"
                className="w-full rounded-full bg-[#1c1917] text-[#faf7f2] hover:bg-[#2c2824]"
                onClick={handleBeginSetup}
                data-testid="guide-welcome-setup"
              >
                Start guided setup
              </Button>
              <Button type="button" variant="outline" className="w-full rounded-full" onClick={handleExplore}>
                Explore on my own
              </Button>
              <Button type="button" variant="ghost" className="w-full rounded-full text-[#5c534a]" onClick={handleViewGuide}>
                View feature guide
              </Button>
            </div>
          )}

          {!isLast ? (
            <p className="mt-4 text-center text-[11px] text-muted-foreground">
              Reopen anytime from the Guide menu.
            </p>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default GuideWelcomeModal;
